/**
 * Expense Categories — derived view of the forecast, organized by GL category.
 *
 * INVARIANT: sum across all categories for any (year, month) ===
 *            sum across all child depts for the same (year, month)
 *
 * Achieved by construction: each category's monthly value is computed as
 *   sum over all depts of: dept_monthly[year][month] × dept_allocation[dept][category]
 *
 * Each dept's allocation row sums to 1.0 (100%). So summing across categories
 * for a given (dept, year, month) = dept_monthly[year][month] × 1.0 = exact match.
 *
 * Sum across all (dept, category) = sum across all dept = source of truth.
 * Math always maths.
 */

/**
 * Each category owns a chart of GL accounts whose proportions sum to 1.0.
 * GL value = (category total) × (within-category proportion).
 * Subtotal of accounts within category === category total === dept allocation. Math ties.
 */
export const EXPENSE_CATEGORIES = [
  {
    id: 'personnel', label: 'Personnel', accent: '#1FAE7C',
    accounts: [
      { code: '61010', name: 'Salaries',                share: 0.75  },
      { code: '61050', name: 'Quebec Payroll Taxes',    share: 0.10  },
      { code: '61020', name: 'Bonus',                   share: 0.05  },
      { code: '61060', name: 'Employee Benefits — Health', share: 0.04 },
      { code: '61040', name: 'Federal Payroll Taxes',   share: 0.02  },
      { code: '61070', name: 'Employee Benefits — Cellphone', share: 0.02 },
      { code: '61080', name: 'Employee Benefits — Gym', share: 0.015 },
      { code: '61055', name: 'Payroll Taxes (US)',      share: 0.005 },
    ],
  },
  {
    id: 'engineering', label: 'Engineering & R&D', accent: '#FF6B47',
    accounts: [
      { code: '51020', name: 'Engineering Consulting Fees', share: 0.35 },
      { code: '51070', name: 'External Testing Costs',      share: 0.25 },
      { code: '51011', name: 'Device Components',           share: 0.18 },
      { code: '52100', name: 'Animal Testing',              share: 0.10 },
      { code: '52200', name: 'EFS — Clinical Trial Costs',  share: 0.10 },
      { code: '53100', name: 'Certifications & Compliance', share: 0.02 },
    ],
  },
  {
    id: 'operations', label: 'Operations & Manufacturing', accent: '#3B82F6',
    accounts: [
      { code: '51040', name: 'Tools & Machine Tooling',     share: 0.30 },
      { code: '51030', name: 'Consumables',                 share: 0.25 },
      { code: '51050', name: 'Machine Repairs & Maintenance', share: 0.15 },
      { code: '51055', name: 'Subcontractor — Manufacturing', share: 0.15 },
      { code: '51060', name: 'Small Machines',              share: 0.10 },
      { code: '51025', name: 'Fixturing & Jigs',            share: 0.05 },
    ],
  },
  {
    id: 'capex', label: 'CapEx', accent: '#8B5CF6',
    accounts: [
      { code: '15030', name: 'Machinery & Equipment',       share: 0.55 },
      { code: '15040', name: 'Furniture',                   share: 0.20 },
      { code: '15050', name: 'Computer Equipment',          share: 0.15 },
      { code: '15010', name: 'Leasehold Improvements',      share: 0.10 },
    ],
  },
  {
    id: 'facilities', label: 'Facilities & Office', accent: '#06B6D4',
    accounts: [
      { code: '65010', name: 'Rent',                        share: 0.70 },
      { code: '65040', name: 'Office Cleaning & Maintenance', share: 0.15 },
      { code: '66060', name: 'Office & Team Supplies',      share: 0.10 },
      { code: '65030', name: 'Internet',                    share: 0.05 },
    ],
  },
  {
    id: 'software', label: 'Software & IT', accent: '#10B981',
    accounts: [
      { code: '66010', name: 'Software Subscriptions',      share: 0.55 },
      { code: '66040', name: 'Customs, Duties, Brokerage',  share: 0.30 },
      { code: '66030', name: 'IT Supplies & Small Equipment', share: 0.10 },
      { code: '66020', name: 'Professional Licenses',       share: 0.05 },
    ],
  },
  {
    id: 'professional', label: 'Professional Services', accent: '#F59E0B',
    accounts: [
      { code: '62010', name: 'Contractor & Consulting Fees', share: 0.35 },
      { code: '63020', name: 'Legal & Immigration',         share: 0.25 },
      { code: '63010', name: 'Corporate Insurance',         share: 0.15 },
      { code: '63021', name: 'Patent Management & Attorney', share: 0.15 },
      { code: '63030', name: 'Audit & Tax',                 share: 0.10 },
    ],
  },
  {
    id: 'people', label: 'People & Team', accent: '#EC4899',
    accounts: [
      { code: '64020', name: 'Recruiting Fees',             share: 0.40 },
      { code: '64060', name: 'Team Events & Gifts',         share: 0.30 },
      { code: '64050', name: 'Team Training',               share: 0.20 },
      { code: '64070', name: 'Team Meals',                  share: 0.10 },
    ],
  },
  {
    id: 'travel', label: 'Travel & Conferences', accent: '#A78BFA',
    accounts: [
      { code: '67010', name: 'Travel & Hotel',              share: 0.65 },
      { code: '67030', name: 'Business Meals',              share: 0.25 },
      { code: '67040', name: 'Conferences',                 share: 0.10 },
    ],
  },
  {
    id: 'banking', label: 'Banking & Other', accent: '#64748B',
    accounts: [
      { code: '99000', name: 'Bank Charges',                share: 1.00 },
    ],
  },
];

// Verify each category's account shares sum to 1.0
(function verifyAccountShares() {
  const errors = [];
  EXPENSE_CATEGORIES.forEach((cat) => {
    const sum = cat.accounts.reduce((s, a) => s + a.share, 0);
    if (Math.abs(sum - 1.0) > 1e-9) {
      errors.push(`Category "${cat.id}": account shares sum to ${sum.toFixed(6)} (must be 1.0)`);
    }
  });
  if (errors.length > 0) {
    // eslint-disable-next-line no-console
    console.error('[expenseCategories] Account share errors:', errors);
  }
})();

/**
 * Per-dept allocation across categories.
 * Each row MUST sum to exactly 1.0. Verified by the assertion below.
 *
 * Allocations are illustrative demo splits per dept. They reflect each
 * dept's typical spending mix.
 */
export const DEPT_CATEGORY_ALLOCATIONS = {
  // Engineering = nearly all salaries (only Salaries + Payroll Taxes)
  'Engineering':              { personnel: 1.00 },

  // Electrical & Software = salaries + tooling/components + small machinery
  'Electrical & Softwares':   { personnel: 0.50, operations: 0.25, engineering: 0.10, capex: 0.05, software: 0.05, facilities: 0.05 },

  // Mechanical Design = salaries + heavy engineering consulting + fixturing
  'Mechanical Design':        { personnel: 0.40, engineering: 0.30, operations: 0.15, capex: 0.05, software: 0.05, facilities: 0.05 },

  // Operations (the line) = director salary + device components
  'Operations':               { personnel: 0.50, engineering: 0.50 },

  // Machine Shop = salaries + tooling + machinery + repairs
  'Machine Shop & Metrology':             { personnel: 0.40, operations: 0.25, capex: 0.25, software: 0.05, facilities: 0.05 },

  // Cleanroom = salaries + consumables + small machines
  'Cleanroom':                { personnel: 0.40, operations: 0.30, capex: 0.20, software: 0.05, facilities: 0.05 },

  // Supply Chain = salaries + customs/duties + supplies
  'Supply Chain':             { personnel: 0.55, software: 0.30, operations: 0.10, facilities: 0.05 },

  // Facilities = salaries + rent + cleaning + leasehold improvements
  'Facilities':               { personnel: 0.30, facilities: 0.55, capex: 0.10, operations: 0.05 },

  // IT = salaries + software + computer equipment
  'IT':                       { personnel: 0.55, software: 0.35, capex: 0.10 },

  // Design Control & Testing = salaries + heavy external testing
  'Design Control & Testing': { personnel: 0.65, engineering: 0.30, operations: 0.05 },

  // Quality = salaries + bonus + certifications
  'Quality':                  { personnel: 0.75, professional: 0.15, software: 0.10 },

  // Clinical & Regulatory = salaries + animal testing + clinical trial costs + travel
  'Clinical & Regulatory':    { personnel: 0.30, engineering: 0.50, travel: 0.10, professional: 0.05, software: 0.05 },

  // Intellectual Property = salaries + patent management + legal
  'Intellectual Property':    { personnel: 0.30, professional: 0.60, software: 0.10 },

  // Finances & Legal = salaries + audit + legal + insurance
  'Finances & Legal':         { personnel: 0.30, professional: 0.60, software: 0.05, banking: 0.05 },

  // People & Culture = salaries + recruiting + training + events + benefits
  'People & Culture':         { personnel: 0.55, people: 0.30, software: 0.10, professional: 0.05 },

  // Executive = salaries + travel + legal + insurance
  'Executive':                { personnel: 0.50, travel: 0.30, professional: 0.10, software: 0.05, operations: 0.05 },
};

// Compile-time check: every allocation row sums to exactly 1.0
(function verifyAllocations() {
  const errors = [];
  Object.entries(DEPT_CATEGORY_ALLOCATIONS).forEach(([dept, allocs]) => {
    const sum = Object.values(allocs).reduce((s, v) => s + v, 0);
    // Tolerate floating-point fuzz of 1e-9
    if (Math.abs(sum - 1.0) > 1e-9) {
      errors.push(`${dept}: sums to ${sum.toFixed(6)} (must be 1.0)`);
    }
    // Verify all keys are valid category ids
    const validIds = new Set(EXPENSE_CATEGORIES.map((c) => c.id));
    Object.keys(allocs).forEach((k) => {
      if (!validIds.has(k)) errors.push(`${dept}: unknown category "${k}"`);
    });
  });
  if (errors.length > 0) {
    // eslint-disable-next-line no-console
    console.error('[expenseCategories] Allocation errors:', errors);
  }
})();

/**
 * Get the % share of a dept's spend that belongs to a category.
 * Returns 0 if not allocated.
 */
export function getAllocation(dept, categoryId) {
  return DEPT_CATEGORY_ALLOCATIONS[dept]?.[categoryId] || 0;
}
